import { api } from '@/lib/api';
import {
  type EventInfo,
  computePublicStats,
  createBlueprintReportCanvas,
  exportBlueprintToPDF,
} from '@/lib/mapExport';
import { useAppSelector } from '@/store/hooks';
import type { LayoutData, Seat, Shape as ShapeType } from '@eventflow/shared-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from 'react-konva';
import { useParams } from 'react-router-dom';
import useImage from 'use-image';

interface SeatMapProps {
  layoutData: LayoutData;
  seats: Seat[];
  selectedSeatIds: string[];
  isInteractive?: boolean;
  lockingSeats?: Set<string>;
  onOpenTable?: (tableId: string, tableLabel: string) => void;
  activeTableId?: string;
  onResetZoom?: () => void; // Callback para notificar que se debe resetear el zoom
  eventInfo?: EventInfo; // Información del evento para el reporte
}

export default function SeatMap({
  layoutData,
  seats,
  selectedSeatIds,
  isInteractive = true,
  lockingSeats = new Set(),
  onOpenTable,
  activeTableId,
  onResetZoom,
  eventInfo,
}: SeatMapProps) {
  const { id: eventId } = useParams<{ id: string }>();
  const organizer = useAppSelector((s) => s.auth.organizer);
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const panStartStagePosRef = useRef<{ x: number; y: number } | null>(null);

  // Memo: estadísticas públicas basadas en datos actuales
  const publicStatsMemo = useMemo(() => {
    return computePublicStats(layoutData, seats);
  }, [layoutData, seats]);
  const [focusedTableId, setFocusedTableId] = useState<string | null>(null);
  const [processTick, setProcessTick] = useState(0);
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number }>({
    visible: false,
    x: 0,
    y: 0,
  });
  const [exportScale, setExportScale] = useState<number>(2); // Escala de exportación por defecto 2x
  const [showScaleSelector, setShowScaleSelector] = useState<boolean>(false); // Mostrar/ocultar selector de escala bajo el botón
  const [contextAdvancedOpen, setContextAdvancedOpen] = useState<boolean>(false); // Submenú avanzado en menú contextual
  const mainMenuRef = useRef<HTMLDivElement | null>(null);
  const advancedBtnRef = useRef<HTMLButtonElement | null>(null);
  const [submenuPos, setSubmenuPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  // Log inicial para verificar qué datos se reciben
  useEffect(() => {
    console.log('📦 [SeatMap] LayoutData recibido:', {
      totalShapes: layoutData.shapes.length,
      shapesWithHidden: layoutData.shapes.filter((s) => s.hidden !== undefined).length,
      shapesWithHiddenTrue: layoutData.shapes.filter((s) => s.hidden === true).length,
      shapesWithHiddenFalse: layoutData.shapes.filter((s) => s.hidden === false).length,
      shapesWithHiddenUndefined: layoutData.shapes.filter((s) => s.hidden === undefined).length,
    });

    // Log de ejemplo de shape para verificar estructura
    if (layoutData.shapes.length > 0) {
      const exampleShape = layoutData.shapes.find((s) => (s as any).tableId === 'TABLE-137');
      if (exampleShape) {
        console.log('🔍 [SeatMap] Ejemplo TABLE-137:', {
          id: exampleShape.id,
          tableId: (exampleShape as any).tableId,
          seatIdentifier: exampleShape.seatIdentifier,
          hidden: exampleShape.hidden,
          hasHiddenProperty: 'hidden' in exampleShape,
        });
      }
    }
  }, [layoutData]);

  const visibleShapes = useMemo(() => {
    // Filtrar shapes: excluir ocultos y overlays
    const visible = layoutData.shapes.filter((shape) => {
      const isHidden = shape.hidden === true;
      const isOverlay = (shape as any).category === 'overlay';
      if (isHidden) {
        console.log(
          '🚫 [SeatMap] Shape oculto filtrado:',
          (shape as any).tableId || shape.seatIdentifier,
          'hidden:',
          shape.hidden
        );
      }
      if (isOverlay) {
        console.log('🎭 [SeatMap] Excluyendo overlay de shapes visibles:', shape.id);
      }
      return !isHidden && !isOverlay;
    });
    console.log('🔍 [SeatMap] Shapes visibles:', visible.length, 'de', layoutData.shapes.length);

    // Verificar si hay shapes con hidden undefined o false
    const shapesWithHiddenUndefined = layoutData.shapes.filter((s) => s.hidden === undefined);
    const shapesWithHiddenFalse = layoutData.shapes.filter((s) => s.hidden === false);
    console.log('📊 [SeatMap] Shapes con hidden undefined:', shapesWithHiddenUndefined.length);
    console.log('📊 [SeatMap] Shapes con hidden false:', shapesWithHiddenFalse.length);

    return visible;
  }, [layoutData.shapes]);

  const hiddenShapes = useMemo(() => {
    // Filtrar shapes ocultos: solo los que tienen hidden === true (mesas)
    const hidden = layoutData.shapes.filter((shape) => {
      const isHidden = shape.hidden === true;
      const isOverlay = (shape as any).category === 'overlay';
      if (isHidden && !isOverlay) {
        console.log(
          '👻 [SeatMap] Shape oculto encontrado:',
          (shape as any).tableId || shape.seatIdentifier,
          'hidden:',
          shape.hidden
        );
      }
      return isHidden && !isOverlay;
    });
    console.log('👻 [SeatMap] Shapes ocultos:', hidden.length, 'de', layoutData.shapes.length);
    if (hidden.length > 0) {
      console.log(
        '👻 [SeatMap] IDs ocultos:',
        hidden.map((s) => s.tableId || s.seatIdentifier)
      );
      // Log detallado de cada shape oculto
      hidden.forEach((shape) => {
        console.log('👻 [SeatMap] Shape oculto detallado:', {
          id: shape.id,
          tableId: (shape as any).tableId,
          seatIdentifier: shape.seatIdentifier,
          hidden: shape.hidden,
          type: shape.type,
          x: shape.x,
          y: shape.y,
        });
      });
    } else {
      console.warn('⚠️ [SeatMap] NO se encontraron shapes ocultos, pero debería haber alguno');
      // Verificar si hay shapes con hidden en cualquier estado
      const allHiddenValues = layoutData.shapes.map((s) => ({
        id: s.id,
        tableId: (s as any).tableId,
        hidden: s.hidden,
      }));
      console.log(
        '📋 [SeatMap] Todos los valores de hidden:',
        allHiddenValues.filter((s) => s.hidden !== undefined)
      );
    }
    return hidden;
  }, [layoutData.shapes]);

  // Guardar estado original del zoom para restaurar al cerrar bottom sheet
  const originalZoomRef = useRef<{ scale: number; pos: { x: number; y: number } } | null>(null);

  // Cargar imagen de fondo
  const [bgImage] = useImage(layoutData.backgroundImage || '', 'anonymous');

  // Overlays para ocultar áreas libres en pública
  const overlays = useMemo(
    () => layoutData.shapes.filter((s: any) => s.category === 'overlay'),
    [layoutData.shapes]
  );

  // Responsive canvas size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = Math.min(width * 0.75, 600); // 4:3 aspect ratio, max 600px
        setStageSize({ width, height });

        // Calculate scale to fit layout
        const scaleX = width / layoutData.dimensions.width;
        const scaleY = height / layoutData.dimensions.height;
        setScale(Math.min(scaleX, scaleY));
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [layoutData.dimensions]);

  // Sutil animación de "proceso" para asientos en lockingSeats
  useEffect(() => {
    if (lockingSeats.size === 0) return;
    const id = window.setInterval(() => {
      setProcessTick((t) => (t + 1) % 1000);
    }, 120);
    return () => window.clearInterval(id);
  }, [lockingSeats]);

  // Log seat status summary once when component mounts or seats change
  useEffect(() => {
    if (seats.length > 0) {
      const statusCounts = {
        available: seats.filter((s) => s.status === 'available').length,
        blocked: seats.filter((s) => s.status === 'blocked').length,
        locked: seats.filter((s) => s.status === 'locked').length,
        sold: seats.filter((s) => s.status === 'sold').length,
      };

      console.log('📊 [SeatMap] Resumen de asientos:', statusCounts);

      if (statusCounts.blocked > 0) {
        const blockedSeats = seats.filter((s) => s.status === 'blocked');
        console.log(
          '🚫 [SeatMap] Asientos bloqueados:',
          blockedSeats.map((s) => s.seat_identifier).join(', ')
        );
      }
    }
  }, [seats]);

  // Compute fill color based on TABLE status aggregation
  const getTableSeats = (shape: ShapeType): Seat[] => {
    const tableId = (shape as any).tableId || shape.seatIdentifier;
    return seats.filter(
      (s) =>
        s.table_id === tableId ||
        s.seat_identifier === tableId ||
        s.seat_identifier.startsWith(`${tableId}-`)
    );
  };

  // Eliminado: getShapeBoundingBox (no usado). Si se requiere en el futuro,
  // puede recuperarse desde el historial o calcularse inline donde se necesite.

  const getFillColor = (shape: ShapeType): string => {
    const tableSeats = getTableSeats(shape);

    if (tableSeats.length === 0) {
      // Fallback visual si aún no se han generado asientos desde layout
      return shape.blocked ? '#EF4444' : '#22D3EE';
    }

    const selectedInTable = tableSeats.some((s) => selectedSeatIds.includes(s.id));
    if (selectedInTable) return '#10B981';

    if (shape.blocked) return '#EF4444';

    const counts = {
      available: tableSeats.filter((s) => s.status === 'available').length,
      locked: tableSeats.filter((s) => s.status === 'locked').length,
      sold: tableSeats.filter((s) => s.status === 'sold').length,
      blocked: tableSeats.filter((s) => s.status === 'blocked').length,
    };

    if (counts.sold === tableSeats.length) return '#6B7280';
    if (counts.available > 0) return '#22D3EE';
    if (counts.locked > 0) return '#EAB308';
    if (counts.blocked > 0) return '#EF4444';
    return '#9CA3AF';
  };

  // Mesa: el click se maneja en handleTableClick

  // Get cursor style
  const getCursor = (_shape: ShapeType): string => {
    return isInteractive ? 'pointer' : 'default';
  };

  const renderShape = (shape: ShapeType) => {
    const fillColor = getFillColor(shape);
    const cursor = getCursor(shape);
    const tableId = (shape as any).tableId || shape.seatIdentifier;
    const isDisabled = !isInteractive ? true : false;
    const isProcessing = false;

    const commonProps = {
      fill: fillColor,
      stroke:
        (activeTableId && ((shape as any).tableId || shape.seatIdentifier) === activeTableId) ||
        focusedTableId === tableId
          ? '#3B82F6'
          : isProcessing
            ? '#3B82F6'
            : '#374151',
      strokeWidth:
        (activeTableId && ((shape as any).tableId || shape.seatIdentifier) === activeTableId) ||
        focusedTableId === tableId
          ? 3
          : isProcessing
            ? 3
            : 2,
      opacity: isDisabled ? 0.5 : isProcessing ? 0.7 : 1,
      dash: isProcessing ? [6, 4] : undefined,
      dashOffset: isProcessing ? processTick * 2 : 0,
      onClick: () => handleTableClick(shape),
      onTap: () => handleTableClick(shape),
      // @ts-ignore - Konva types issue
      cursor: isProcessing ? 'wait' : cursor,
      hitStrokeWidth: 20,
      onMouseEnter: (e: any) => {
        const stage = e.target.getStage();
        if (stage) stage.container().style.cursor = isProcessing ? 'wait' : cursor;
      },
      onMouseLeave: (e: any) => {
        const stage = e.target.getStage();
        if (stage) stage.container().style.cursor = 'default';
      },
    };

    if (shape.type === 'circle' && shape.x !== undefined && shape.y !== undefined && shape.radius) {
      return (
        <Circle key={shape.id} {...commonProps} x={shape.x} y={shape.y} radius={shape.radius} />
      );
    }

    if (
      shape.type === 'rect' &&
      shape.x !== undefined &&
      shape.y !== undefined &&
      shape.width &&
      shape.height
    ) {
      return (
        <Rect
          key={shape.id}
          {...commonProps}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          rotation={shape.rotation || 0}
        />
      );
    }

    if (shape.type === 'polygon' && shape.points && shape.points.length > 0) {
      return <Line key={shape.id} {...commonProps} points={shape.points} closed={true} />;
    }

    return null;
  };

  const handleTableClick = (shape: ShapeType) => {
    const tableId = (shape as any).tableId || shape.seatIdentifier;
    const tableLabel = (shape as any).tableLabel || tableId;
    setFocusedTableId(tableId);

    // Guardar estado original del zoom SOLO si no hay zoom previo guardado
    if (!originalZoomRef.current) {
      originalZoomRef.current = { scale, pos: stagePos };
    }

    // Calcular centro del shape para centrar y zoom sutil
    let cx = 0;
    let cy = 0;
    if (shape.type === 'circle' && shape.x !== undefined && shape.y !== undefined) {
      cx = shape.x;
      cy = shape.y;
    } else if (
      shape.type === 'rect' &&
      shape.x !== undefined &&
      shape.y !== undefined &&
      shape.width &&
      shape.height
    ) {
      cx = shape.x + shape.width / 2;
      cy = shape.y + shape.height / 2;
    } else if (shape.type === 'polygon' && shape.points && shape.points.length > 0) {
      const xs = shape.points.filter((_, i) => i % 2 === 0);
      const ys = shape.points.filter((_, i) => i % 2 === 1);
      cx = xs.reduce((a, b) => a + b, 0) / xs.length;
      cy = ys.reduce((a, b) => a + b, 0) / ys.length;
    }

    // Usar el scale original guardado para evitar zoom acumulativo
    const baseScale = originalZoomRef.current.scale;
    // Aumentar zoom más agresivo (1.8x) desde el scale original
    const newScale = Math.min(3, baseScale * 1.8);
    setScale(newScale);

    // Centrar la mesa en el viewport
    const centerX = stageSize.width / 2;
    const centerY = stageSize.height / 2;

    setStagePos({
      x: centerX - cx * newScale,
      y: centerY - cy * newScale,
    });

    onOpenTable?.(tableId, tableLabel);
  };

  // Función pública para resetear el zoom (se llama cuando se cierra el bottom sheet)
  const resetZoom = () => {
    if (originalZoomRef.current) {
      setScale(originalZoomRef.current.scale);
      setStagePos(originalZoomRef.current.pos);
      originalZoomRef.current = null; // Limpiar el estado guardado
    }
    setFocusedTableId(null);
  };

  // Exponer resetZoom al padre mediante callback
  useEffect(() => {
    if (onResetZoom) {
      // Pasar la función de reset al padre
      (window as any).__seatMapResetZoom = resetZoom;
    }
    return () => {
      delete (window as any).__seatMapResetZoom;
    };
  }, [onResetZoom, scale, stagePos]);

  const handleZoomIn = () => {
    // Guardar estado original si no está guardado
    if (!originalZoomRef.current) {
      originalZoomRef.current = { scale, pos: stagePos };
    }

    // Aplicar zoom incremental desde el scale actual
    const newScale = Math.min(3, scale * 1.2);
    setScale(newScale);
  };

  const handleZoomOut = () => {
    // Guardar estado original si no está guardado
    if (!originalZoomRef.current) {
      originalZoomRef.current = { scale, pos: stagePos };
    }

    // Aplicar zoom incremental desde el scale actual
    const newScale = Math.max(0.5, scale * 0.8);
    setScale(newScale);
  };

  const handleZoomReset = () => {
    resetZoom();
  };

  const isZoomed = originalZoomRef.current !== null;

  // Helpers para panning con mouse/touch en fondo
  const getClientXY = (e: any): { x: number; y: number } => {
    if (e?.evt?.touches && e.evt.touches.length > 0) {
      return { x: e.evt.touches[0].clientX, y: e.evt.touches[0].clientY };
    }
    return { x: e?.evt?.clientX ?? 0, y: e?.evt?.clientY ?? 0 };
  };

  const handlePanStart = (e: any) => {
    setIsPanning(true);
    panStartRef.current = getClientXY(e);
    panStartStagePosRef.current = { ...stagePos };
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = 'grabbing';
  };

  const handlePanMove = (e: any) => {
    if (!isPanning || !panStartRef.current || !panStartStagePosRef.current) return;
    const { x, y } = getClientXY(e);
    const dx = x - panStartRef.current.x;
    const dy = y - panStartRef.current.y;
    setStagePos({ x: panStartStagePosRef.current.x + dx, y: panStartStagePosRef.current.y + dy });
  };

  const handlePanEnd = (e: any) => {
    if (!isPanning) return;
    setIsPanning(false);
    panStartRef.current = null;
    panStartStagePosRef.current = null;
    const stage = e?.target?.getStage?.();
    if (stage) stage.container().style.cursor = 'default';
  };

  // Utilidad: centro geométrico de una shape
  const getShapeCenter = (shape: ShapeType): { cx: number; cy: number } => {
    let cx = 0;
    let cy = 0;
    if (shape.type === 'circle' && shape.x !== undefined && shape.y !== undefined) {
      cx = shape.x;
      cy = shape.y;
    } else if (
      shape.type === 'rect' &&
      shape.x !== undefined &&
      shape.y !== undefined &&
      shape.width &&
      shape.height
    ) {
      cx = shape.x + shape.width / 2;
      cy = shape.y + shape.height / 2;
    } else if (shape.type === 'polygon' && shape.points && shape.points.length > 0) {
      const xs = shape.points.filter((_, i) => i % 2 === 0);
      const ys = shape.points.filter((_, i) => i % 2 === 1);
      cx = xs.reduce((a, b) => a + b, 0) / xs.length;
      cy = ys.reduce((a, b) => a + b, 0) / ys.length;
    }
    return { cx, cy };
  };

  // Validación: el centro mapeado por zoom/pan cae en el viewport visible
  const isCenterInViewport = (cx: number, cy: number): boolean => {
    const sx = cx * scale + stagePos.x;
    const sy = cy * scale + stagePos.y;
    return sx >= 0 && sy >= 0 && sx <= stageSize.width && sy <= stageSize.height;
  };

  // Componer canvas del viewport con fondo blanco
  const buildViewportCanvasWithWhiteBg = (scaleFactor = 1) => {
    const stage = stageRef.current as any;
    if (!stage) return null;
    const pixelRatio = Math.max(1, Math.floor(scaleFactor));
    const canvas = stage.toCanvas({ pixelRatio });
    const expectedWidth = Math.round(stage.width() * pixelRatio);
    const expectedHeight = Math.round(stage.height() * pixelRatio);
    const merged = document.createElement('canvas');
    merged.width = expectedWidth;
    merged.height = expectedHeight;
    const mctx = merged.getContext('2d');
    if (!mctx) return null;
    // Fondo blanco para evitar transparencia
    mctx.fillStyle = '#ffffff';
    mctx.fillRect(0, 0, merged.width, merged.height);
    // Dibujar el contenido del Stage encima, ajustando a tamaño esperado
    mctx.drawImage(canvas, 0, 0, merged.width, merged.height);
    return merged;
  };

  // Exportación: genera PNG del Stage y valida dimensiones y contenido antes de descargar
  const exportCurrentViewAsPng = async (scaleFactor = 1) => {
    const stage = stageRef.current as any;
    if (!stage) {
      console.warn('[SeatMap] Stage no disponible para exportación');
      return;
    }

    // Requisitos previos: imagen de fondo y al menos una shape visible en viewport
    const hasBg = !!bgImage;
    const visibleInViewport = visibleShapes.filter((s) => {
      const { cx, cy } = getShapeCenter(s);
      return isCenterInViewport(cx, cy);
    });
    if (!hasBg) {
      alert('No se puede descargar: el mapa base aún no está cargado.');
      return;
    }
    if (visibleInViewport.length === 0) {
      alert('No se puede descargar: no hay mesas visibles dentro de la vista.');
      return;
    }

    // Exportar al tamaño visible del Stage multiplicado por el factor solicitado, con fondo blanco
    const merged = buildViewportCanvasWithWhiteBg(scaleFactor);
    if (!merged) {
      alert('No se puede descargar: error al generar la imagen.');
      return;
    }
    const dataURL = merged.toDataURL('image/png');

    // Validar dimensiones de la imagen generada
    const img = new Image();
    const expectedWidth = Math.round(
      stageRef.current.width() * Math.max(1, Math.floor(scaleFactor))
    );
    const expectedHeight = Math.round(
      stageRef.current.height() * Math.max(1, Math.floor(scaleFactor))
    );
    const ok = await new Promise<boolean>((resolve) => {
      img.onload = () => {
        const dimsOk = img.naturalWidth === expectedWidth && img.naturalHeight === expectedHeight;
        resolve(dimsOk);
      };
      img.onerror = () => resolve(false);
      img.src = dataURL;
    });
    if (!ok) {
      alert('No se puede descargar: la imagen generada no coincide con el tamaño esperado.');
      return;
    }

    // Descarga
    const a = document.createElement('a');
    a.href = dataURL;
    const suffix = Math.max(1, Math.floor(scaleFactor));
    a.download = suffix > 1 ? `mapa-evento-${suffix}x.png` : 'mapa-evento.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Cerrar menú contextual si está abierto
    setContextMenu({ visible: false, x: 0, y: 0 });

    // Logging de exportación
    try {
      if (eventId) {
        await api.post(`/events/${eventId}/log-export`, {
          exportType: 'image',
          scale: Math.max(1, Math.floor(scaleFactor)),
          source: 'seat_map',
          viewport: {
            x: stagePos.x,
            y: stagePos.y,
            width: stageRef.current.width(),
            height: stageRef.current.height(),
          },
        });
      }
    } catch (err) {
      console.warn('[SeatMap] No se pudo registrar el log de exportación:', err);
    }
  };

  // Copiar imagen al portapapeles (soporta escala)
  const copyCurrentViewToClipboard = async (scaleFactor = 1) => {
    const stage = stageRef.current as any;
    if (!stage) return;
    const merged = buildViewportCanvasWithWhiteBg(scaleFactor);
    if (!merged) return;
    const blob: Blob | null = await new Promise((resolve) =>
      merged.toBlob((b) => resolve(b), 'image/png')
    );
    if (!blob) {
      alert('No se pudo copiar la imagen.');
      return;
    }
    try {
      // @ts-ignore - ClipboardItem puede no estar tipado en TS
      const item = new ClipboardItem({ [blob.type]: blob });
      await navigator.clipboard.write([item]);
      setContextMenu({ visible: false, x: 0, y: 0 });
      const suffix = Math.max(1, Math.floor(scaleFactor));
      alert(`Imagen copiada al portapapeles (${suffix}x).`);

      // Logging de copia
      try {
        if (eventId) {
          await api.post(`/events/${eventId}/log-export`, {
            exportType: 'copy',
            scale: Math.max(1, Math.floor(scaleFactor)),
            source: 'seat_map',
            viewport: {
              x: stagePos.x,
              y: stagePos.y,
              width: stageRef.current.width(),
              height: stageRef.current.height(),
            },
          });
        }
      } catch (err) {
        console.warn('[SeatMap] No se pudo registrar el log de copia:', err);
      }
    } catch (err) {
      console.error('Clipboard write error:', err);
      alert('No se pudo copiar al portapapeles en este navegador.');
    }
  };

  // Exportar imagen con estadísticas (solo Organizadores) - Diseño blueprint profesional
  const exportStatsImage = async (quality = 1) => {
    const stage = stageRef.current as any;
    if (!stage) return;
    if (!organizer) {
      alert('Esta función requiere rol de Organizador.');
      return;
    }

    // quality: 1 = normal (pixelRatio 2), 3 = alta calidad (pixelRatio 3)
    const pixelRatio = quality === 3 ? 3 : 2;
    const stats = publicStatsMemo;

    // Usar información del evento si está disponible
    const eventInfoToUse: EventInfo = eventInfo || {
      title: 'Evento',
      venue: undefined,
      date: undefined,
      time: undefined,
    };

    // Mostrar indicador de carga
    console.log(
      `📄 Generando reporte ${quality === 3 ? '(alta calidad)' : ''}... Por favor espera.`
    );

    // Usar setTimeout para permitir que el UI se actualice
    setTimeout(async () => {
      try {
        // Capturar el mapa visual
        const mapCanvas = buildViewportCanvasWithWhiteBg(pixelRatio);
        if (!mapCanvas) {
          alert('No se puede exportar: error al generar el mapa.');
          return;
        }

        // Crear canvas con diseño blueprint profesional
        const blueprintCanvas = createBlueprintReportCanvas(mapCanvas, stats, 1, eventInfoToUse);
        if (!blueprintCanvas) {
          alert('No se puede exportar: error al generar la imagen.');
          return;
        }

        const dataURL = blueprintCanvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataURL;
        const qualityLabel = quality === 3 ? '-alta-calidad' : '';
        a.download = `reporte-mapa-evento${qualityLabel}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        console.log('✅ Reporte exportado exitosamente!');
      } catch (error) {
        console.error('❌ Error al exportar:', error);
        alert('Error al exportar el reporte.');
      }
      setContextMenu({ visible: false, x: 0, y: 0 });
      setContextAdvancedOpen(false);
    }, 100);

    // Logging de exportación con estadísticas
    try {
      if (eventId) {
        await api.post(`/events/${eventId}/log-export`, {
          exportType: 'stats',
          scale: pixelRatio,
          source: 'seat_map',
          viewport: {
            x: stagePos.x,
            y: stagePos.y,
            width: stageRef.current.width(),
            height: stageRef.current.height(),
          },
          stats,
        });
      }
    } catch (err) {
      console.warn('[SeatMap] No se pudo registrar el log de estadísticas:', err);
    }
  };

  // Exportar estadísticas como PDF (solo Organizadores) - Máxima calidad
  const exportStatsPDF = async () => {
    const stage = stageRef.current as any;
    if (!stage) return;
    if (!organizer) {
      alert('Esta función requiere rol de Organizador.');
      return;
    }

    // Usar máxima calidad (pixelRatio 3) para PDF
    const pixelRatio = 3;
    const stats = publicStatsMemo;

    // Usar información del evento si está disponible
    const eventInfoToUse: EventInfo = eventInfo || {
      title: 'Evento',
      venue: undefined,
      date: undefined,
      time: undefined,
    };

    // Mostrar indicador de carga
    console.log('📄 Generando reporte PDF (máxima calidad)... Por favor espera.');

    // Usar setTimeout para permitir que el UI se actualice
    setTimeout(async () => {
      try {
        // Capturar el mapa visual en máxima calidad
        const mapCanvas = buildViewportCanvasWithWhiteBg(pixelRatio);
        if (!mapCanvas) {
          alert('No se puede exportar: error al generar el mapa.');
          return;
        }

        // Crear canvas con diseño blueprint profesional
        const blueprintCanvas = createBlueprintReportCanvas(mapCanvas, stats, 1, eventInfoToUse);
        if (!blueprintCanvas) {
          alert('No se puede exportar: error al generar la imagen.');
          return;
        }

        // Exportar a PDF
        exportBlueprintToPDF(blueprintCanvas, 'reporte-mapa-evento.pdf');

        console.log('✅ Reporte PDF exportado exitosamente!');
      } catch (error) {
        console.error('❌ Error al exportar PDF:', error);
        alert('Error al exportar el reporte PDF.');
      }
      setContextMenu({ visible: false, x: 0, y: 0 });
      setContextAdvancedOpen(false);
    }, 100);

    // Logging de exportación
    try {
      if (eventId) {
        await api.post(`/events/${eventId}/log-export`, {
          exportType: 'stats',
          scale: pixelRatio,
          source: 'seat_map',
          viewport: {
            x: stageRef.current!.x(),
            y: stageRef.current!.y(),
            width: stageRef.current!.width(),
            height: stageRef.current!.height(),
          },
          stats: {
            totalSeats: stats.totalAsientos,
            available: stats.asientosDisponibles,
            sold: stats.asientosVendidosConfirmados,
            locked: stats.asientosReservadosPendientes,
            blocked: stats.asientosBloqueados,
          },
        });
      }
    } catch (err) {
      console.warn('[SeatMap] No se pudo registrar el log de exportación PDF:', err);
    }
  };

  // Mostrar menú contextual personalizado
  const handleContextMenu = (e: any) => {
    e.evt?.preventDefault?.();
    const x = e?.evt?.clientX ?? 0;
    const y = e?.evt?.clientY ?? 0;
    setContextAdvancedOpen(false);
    setContextMenu({ visible: true, x, y });
    // Posición por defecto del submenú al abrir (se recalcula al pulsar "Opciones avanzadas")
    const defaultLeft = x + 220;
    setSubmenuPos({ left: defaultLeft, top: y });
  };

  // Cerrar menú contextual con Escape
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setContextMenu({ visible: false, x: 0, y: 0 });
        setContextAdvancedOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div ref={containerRef} className="w-full space-y-3">
      {/* Leyenda de estados */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Leyenda</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#22D3EE' }}></div>
            <span className="text-gray-700">Disponible</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#10B981' }}></div>
            <span className="text-gray-700">Seleccionado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#EAB308' }}></div>
            <span className="text-gray-700">Reservado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#6B7280' }}></div>
            <span className="text-gray-700">Vendido</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#EF4444' }}></div>
            <span className="text-gray-700">Bloqueado</span>
          </div>
        </div>
      </div>

      {/* Mapa de asientos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
        {/* Controles de zoom */}
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
          {/* Botón de zoom out - solo aparece cuando hay zoom */}
          {isZoomed && (
            <button
              onClick={handleZoomReset}
              className="bg-white hover:bg-gray-50 border border-gray-300 rounded-lg p-2 shadow-md transition-colors"
              title="Restaurar zoom al estado original"
              aria-label="Restaurar zoom al estado original"
            >
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          )}

          {/* Controles de zoom */}
          <div className="flex flex-col gap-1 bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden">
            <button
              onClick={handleZoomIn}
              className="bg-white hover:bg-gray-50 p-2 transition-colors"
              title="Acercar"
              aria-label="Acercar"
            >
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                />
              </svg>
            </button>
            <div className="border-t border-gray-300"></div>
            <button
              onClick={handleZoomOut}
              className="bg-white hover:bg-gray-50 p-2 transition-colors"
              title="Alejar"
              aria-label="Alejar"
            >
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                />
              </svg>
            </button>
          </div>
          {/* Contenedor relativo para unir el dropdown al botón de imagen */}
          <div className="relative">
            {/* Botón que despliega el selector de escala (elimina descarga directa) */}
            <button
              onClick={() => setShowScaleSelector((v) => !v)}
              className="bg-white hover:bg-gray-50 border border-gray-300 rounded-lg p-2 shadow-md transition-colors"
              title={
                showScaleSelector
                  ? 'Ocultar opciones de escala'
                  : 'Descargar mapa de mesas/asientos'
              }
              aria-label={
                showScaleSelector
                  ? 'Ocultar opciones de escala'
                  : 'Descargar mapa de mesas/asientos'
              }
            >
              {/* Icono de imagen (foto) */}
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 5a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V5z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 15l3-4 4 6H6"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 8a2 2 0 110 4 2 2 0 010-4z"
                />
              </svg>
            </button>

            {/* Selector 1x/2x/3x unido al botón, con animación de deslizamiento */}
            <div
              className={`absolute right-0 mt-1 origin-top transform transition-all duration-200 ${showScaleSelector ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0 pointer-events-none'}`}
            >
              <div className="flex flex-col gap-1 bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden">
                {[1, 2, 3].map((s) => (
                  <button
                    key={`escala-${s}`}
                    onClick={() => {
                      setExportScale(s);
                      exportCurrentViewAsPng(s);
                      setShowScaleSelector(false);
                    }}
                    className={`px-3 py-2 transition-colors ${exportScale === s ? 'bg-gray-100 text-gray-800' : 'bg-white hover:bg-gray-50 text-gray-700'}`}
                    title={`${s}x`}
                    aria-label={`${s}x`}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Copiar PNG al portapapeles fijo en 2x (icono actualizado) */}
          <button
            onClick={() => copyCurrentViewToClipboard(2)}
            className="bg-white hover:bg-gray-50 border border-gray-300 rounded-lg p-2 shadow-md transition-colors"
            title={`Copiar imagen al portapapeles`}
            aria-label={`Copiar imagen al portapapeles`}
          >
            {/* Nuevo icono: documentos duplicados (representa copiar) */}
            <svg
              className="w-5 h-5 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 6a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2h-8a2 2 0 01-2-2V6z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8a2 2 0 012-2h8M6 8v10a2 2 0 002 2h8"
              />
            </svg>
          </button>
        </div>

        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          scaleX={scale}
          scaleY={scale}
          x={stagePos.x}
          y={stagePos.y}
          onContextMenu={handleContextMenu}
        >
          <Layer>
            {/* Imagen de fondo */}
            {bgImage && (
              <KonvaImage
                image={bgImage}
                width={layoutData.dimensions.width}
                height={layoutData.dimensions.height}
                listening={false}
                opacity={0.85}
              />
            )}

            {/* Fondo interactivo para panning (debajo de las shapes) */}
            <Rect
              x={0}
              y={0}
              width={layoutData.dimensions.width}
              height={layoutData.dimensions.height}
              fill="#000"
              opacity={0}
              onMouseEnter={(e: any) => {
                const stage = e.target.getStage();
                if (stage && !isPanning) stage.container().style.cursor = 'grab';
              }}
              onMouseLeave={(e: any) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = 'default';
                handlePanEnd(e);
              }}
              onMouseDown={handlePanStart}
              onMouseMove={handlePanMove}
              onMouseUp={handlePanEnd}
              onTouchStart={handlePanStart}
              onTouchMove={handlePanMove}
              onTouchEnd={handlePanEnd}
            />

            {/* Render visible shapes ONLY - NO renderizar shapes ocultos ni overlays */}
            {visibleShapes.map((shape) => renderShape(shape))}

            {/* Menú contextual: se renderiza como overlay HTML más abajo */}

            {/* Render seat labels */}
            {visibleShapes.map((shape) => {
              // Mostrar etiqueta de mesa

              let x = 0;
              let y = 0;
              let maxWidth = 100; // Ancho máximo por defecto

              if (
                shape.type === 'circle' &&
                shape.x !== undefined &&
                shape.y !== undefined &&
                shape.radius
              ) {
                x = shape.x;
                y = shape.y;
                // Para círculos, usar el diámetro como ancho máximo (con un margen del 80%)
                maxWidth = shape.radius * 2 * 0.8;
              } else if (
                shape.type === 'rect' &&
                shape.x !== undefined &&
                shape.y !== undefined &&
                shape.width &&
                shape.height
              ) {
                x = shape.x + shape.width / 2;
                y = shape.y + shape.height / 2;
                // Para rectángulos, usar el menor entre width y height (con un margen del 90%)
                maxWidth = Math.min(shape.width, shape.height) * 0.9;
              } else if (shape.type === 'polygon' && shape.points) {
                // Calculate centroid of polygon
                const xCoords = shape.points.filter((_, i) => i % 2 === 0);
                const yCoords = shape.points.filter((_, i) => i % 2 === 1);
                x = xCoords.reduce((a, b) => a + b, 0) / xCoords.length;
                y = yCoords.reduce((a, b) => a + b, 0) / yCoords.length;
                // Para polígonos, calcular el ancho mínimo basado en los puntos
                const minX = Math.min(...xCoords);
                const maxX = Math.max(...xCoords);
                const minY = Math.min(...yCoords);
                const maxY = Math.max(...yCoords);
                maxWidth = Math.min(maxX - minX, maxY - minY) * 0.9;
              }

              const tableId = (shape as any).tableId || shape.seatIdentifier;
              const tableLabel = (shape as any).tableLabel || tableId;

              // Calcular fontSize dinámico basado en el ancho disponible y la longitud del texto
              // Usar una proporción aproximada: fontSize * textLength ≈ maxWidth
              // Con un tamaño mínimo de 10 y máximo de 16
              const estimatedFontSize = Math.max(
                10,
                Math.min(16, Math.floor(maxWidth / (tableLabel.length * 0.7)))
              );

              // Centrar el texto: offsetX debe ser la mitad del ancho para centrar horizontalmente
              // offsetY debe ser aproximadamente la mitad de la altura de línea para centrar verticalmente
              const offsetX = maxWidth / 2;
              const offsetY = estimatedFontSize / 2;

              return (
                <Text
                  key={`label-${shape.id}`}
                  x={x}
                  y={y}
                  text={tableLabel}
                  fontSize={estimatedFontSize}
                  fontStyle="bold"
                  fill="#000000"
                  align="center"
                  verticalAlign="middle"
                  offsetX={offsetX}
                  offsetY={offsetY}
                  width={maxWidth}
                  wrap="word"
                  ellipsis={true}
                  listening={false}
                />
              );
            })}
          </Layer>

          {/* Layer separado para máscaras de shapes ocultos - se renderiza DESPUÉS para estar por encima */}
          {hiddenShapes.length > 0 && (
            <>
              <Layer>
                {hiddenShapes.map((shape) => {
                  const maskFill = (shape as any).maskFill || '#ffffff';
                  const maskOpacity = (shape as any).maskOpacity ?? 1;
                  // Usar margen negativo por defecto para crear un pequeño "gap" visual
                  const defaultGap = -8; // px
                  const margin =
                    typeof (shape as any).maskMargin === 'number'
                      ? (shape as any).maskMargin
                      : defaultGap;
                  if (
                    shape.type === 'circle' &&
                    shape.x !== undefined &&
                    shape.y !== undefined &&
                    shape.radius
                  ) {
                    return (
                      <Circle
                        key={`mask-${shape.id}`}
                        x={shape.x}
                        y={shape.y}
                        radius={Math.max(4, (shape.radius as number) + margin)}
                        fill={maskFill}
                        opacity={maskOpacity}
                        listening={false}
                      />
                    );
                  }
                  if (
                    shape.type === 'rect' &&
                    shape.x !== undefined &&
                    shape.y !== undefined &&
                    shape.width &&
                    shape.height
                  ) {
                    return (
                      <Rect
                        key={`mask-${shape.id}`}
                        x={(shape.x as number) - margin}
                        y={(shape.y as number) - margin}
                        width={Math.max(8, (shape.width as number) + margin * 2)}
                        height={Math.max(8, (shape.height as number) + margin * 2)}
                        fill={maskFill}
                        opacity={maskOpacity}
                        listening={false}
                      />
                    );
                  }
                  if (shape.type === 'polygon' && shape.points && shape.points.length > 0) {
                    // Para polígonos, aplicamos la máscara con la misma geometría (sin expansión)
                    return (
                      <Line
                        key={`mask-${shape.id}`}
                        points={shape.points}
                        closed
                        fill={maskFill}
                        opacity={maskOpacity}
                        listening={false}
                      />
                    );
                  }
                  return null;
                })}
              </Layer>
            </>
          )}

          {/* Overlays por encima de todo (no interactivos) */}
          {overlays.length > 0 && (
            <Layer>
              {overlays.map((shape: any) => {
                const fill = shape.overlayFill || '#ffffff';
                const opacity = shape.overlayOpacity ?? 1;
                if (
                  shape.type === 'circle' &&
                  shape.x !== undefined &&
                  shape.y !== undefined &&
                  shape.radius
                ) {
                  return (
                    <Circle
                      key={`overlay-${shape.id}`}
                      x={shape.x}
                      y={shape.y}
                      radius={shape.radius}
                      fill={fill}
                      opacity={opacity}
                      listening={false}
                    />
                  );
                }
                if (
                  shape.type === 'rect' &&
                  shape.x !== undefined &&
                  shape.y !== undefined &&
                  shape.width &&
                  shape.height
                ) {
                  return (
                    <Rect
                      key={`overlay-${shape.id}`}
                      x={shape.x}
                      y={shape.y}
                      width={shape.width}
                      height={shape.height}
                      fill={fill}
                      opacity={opacity}
                      listening={false}
                    />
                  );
                }
                if (shape.type === 'polygon' && shape.points && shape.points.length > 0) {
                  return (
                    <Line
                      key={`overlay-${shape.id}`}
                      points={shape.points}
                      closed
                      fill={fill}
                      opacity={opacity}
                      listening={false}
                    />
                  );
                }
                return null;
              })}
            </Layer>
          )}
        </Stage>

        {/* Menú contextual (overlay HTML) */}
        {contextMenu.visible && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                setContextMenu({ visible: false, x: 0, y: 0 });
                setContextAdvancedOpen(false);
              }}
            />
            {/* Menú principal */}
            <div
              ref={mainMenuRef}
              className="fixed z-50 bg-neutral-900 text-white rounded-xl shadow-lg border border-neutral-700 overflow-hidden"
              style={{ left: contextMenu.x, top: contextMenu.y }}
            >
              <button
                onClick={() => {
                  exportCurrentViewAsPng(2);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
              >
                Guardar imagen...
              </button>
              {organizer && (
                <>
                  <div className="border-t border-neutral-800" />
                  <button
                    onClick={() => {
                      exportStatsImage(1);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
                  >
                    Guardar con estadísticas
                  </button>
                </>
              )}
              <div className="border-t border-neutral-800" />
              <button
                onClick={() => {
                  copyCurrentViewToClipboard(2);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
              >
                Copiar imagen
              </button>
              <div className="border-t border-neutral-800" />
              <button
                ref={advancedBtnRef}
                onClick={() => {
                  // Calcular posición exacta del submenú pegado y alineado con "Opciones avanzadas"
                  const menuRect = mainMenuRef.current?.getBoundingClientRect();
                  const advRect = advancedBtnRef.current?.getBoundingClientRect();
                  if (menuRect && advRect) {
                    setSubmenuPos({
                      left: Math.round(menuRect.right),
                      top: Math.round(advRect.top),
                    });
                  } else {
                    setSubmenuPos({ left: contextMenu.x + 220, top: contextMenu.y });
                  }
                  setContextAdvancedOpen((v) => !v);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
              >
                Opciones avanzadas ▷
              </button>
            </div>
            {/* Submenú avanzado */}
            {contextAdvancedOpen && (
              <div
                className="fixed z-50 bg-neutral-900 text-white rounded-xl shadow-lg border border-neutral-700 overflow-hidden"
                style={{ left: submenuPos.left, top: submenuPos.top }}
              >
                <button
                  onClick={() => exportCurrentViewAsPng(3)}
                  className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
                >
                  Guardar imagen (maxima calidad)
                </button>
                <div className="border-t border-neutral-800" />
                <button
                  onClick={() => copyCurrentViewToClipboard(3)}
                  className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
                >
                  Copiar imagen (maxima calidad)
                </button>
                {organizer && (
                  <>
                    <div className="border-t border-neutral-800" />
                    <button
                      onClick={() => exportStatsImage(3)}
                      className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
                    >
                      Guardar estadísticas PNG (maxima calidad)
                    </button>
                    <div className="border-t border-neutral-800" />
                    <button
                      onClick={exportStatsPDF}
                      className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
                    >
                      Guardar estadísticas PDF (maxima calidad)
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
